import React from 'react';
import { emailTemplates, EmailTemplate } from '../../templates/EmailTemplates';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface TemplateGalleryProps {
  onSelectTemplate: (template: EmailTemplate) => void;
}

export const TemplateGallery: React.FC<TemplateGalleryProps> = ({ onSelectTemplate }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState('all');
  
  const filteredTemplates = React.useMemo(() => {
    let filtered = [...emailTemplates];
    
    if (searchTerm) {
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory !== 'all') {
      // You can implement category filtering here if you add categories to templates
    }
    
    return filtered;
  }, [searchTerm, selectedCategory]);
  
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      <div className="flex gap-2 overflow-x-auto pb-2">
        <Button
          variant={selectedCategory === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory('all')}
        >
          All
        </Button>
        <Button
          variant={selectedCategory === 'welcome' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory('welcome')}
        >
          Welcome
        </Button>
        <Button
          variant={selectedCategory === 'newsletter' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory('newsletter')}
        >
          Newsletter
        </Button>
        <Button
          variant={selectedCategory === 'promotion' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory('promotion')}
        >
          Promotion
        </Button>
        <Button
          variant={selectedCategory === 'announcement' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory('announcement')}
        >
          Announcement
        </Button>
      </div>
      
      <ScrollArea className="h-[400px]">
        <div className="grid grid-cols-2 gap-4 pr-4">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="overflow-hidden cursor-pointer hover:border-primary" onClick={() => onSelectTemplate(template)}>
              <div className="aspect-video bg-muted flex items-center justify-center text-4xl">
                {template.thumbnail}
              </div>
              <CardContent className="p-3">
                <h3 className="font-medium text-sm">{template.name}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{template.description}</p>
              </CardContent>
            </Card>
          ))}
          
          {filteredTemplates.length === 0 && (
            <div className="col-span-2 py-8 text-center text-muted-foreground">
              No templates found matching your search.
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}; 